import React, { useState } from 'react';
import { Flex, Text, Button, Heading, Dialog, Tooltip } from '@scale/scaleui-radix';
import { TrustedTagQualityType, ITagJson } from '@scale/shared/types/tag';
import {
  useCreateTag,
  useUpdateTags,
} from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import _ from 'lodash';
import { TrustedTagForm } from 'react-scale/corp/views/CorpProjectGroupManager/components/ContributorQuality/TrustedTags/TrustedTagForm';

export interface QualityTagDialogProps {
  currentTag: string;
  userId: string;
  setShowAddTagModal: (showAddTagModal: boolean) => void;
  onCreateTag: (tag: ITagJson) => void;
}

export const QualityTagDialog: React.FC<QualityTagDialogProps> = ({
  currentTag,
  userId,
  setShowAddTagModal,
  onCreateTag,
}: QualityTagDialogProps) => {
  const [description, setDescription] = useState('');
  const [qualityType, setQuality] = useState(TrustedTagQualityType.N_A);
  const [projects, setProjects] = useState<string[]>([]);
  const [workerSkills, setWorkerSkills] = useState<string[]>([]);
  const [tagName, setTagName] = useState(currentTag);
  const [tagsToAdd, setTagsToAdd] = useState<string[]>([]);

  const { mutateAsync: updateTags, isLoading: isUpdatingTags } = useUpdateTags({
    userId,
    tagsToAdd,
    tagsToRemove: [],
  });

  const { mutateAsync: createTag, isLoading: isCreatingTag } = useCreateTag({
    name: tagName,
    userId,
    metadata: undefined,
    description,
    trustedTagMetadata: {
      qualityType,
      projects,
      workerSkills,
    },
  });

  const useCreateAndUpdateTag = async () => {
    const result = await createTag();
    if (!_.isNil(result._id)) {
      setTagsToAdd([result._id]);
      await updateTags();
      onCreateTag({
        _id: result._id,
        name: tagName,
      });
      setShowAddTagModal(false);
    }
  };

  // force description if they choose a qualityType
  const emptyDescriptionIfQualityTag =
    qualityType !== TrustedTagQualityType.N_A && description.length === 0;
  const allowSaveTooltip = emptyDescriptionIfQualityTag
    ? 'Description is required if quality type is set'
    : '';

  return (
    <Flex direction="column" gap="5">
      <Heading>Create a new tag</Heading>
      <Text>
        Your input acts as ground truth data for analytics and helps other operators understand this
        tag.
      </Text>

      <TrustedTagForm
        tagName={tagName}
        description={description}
        qualityType={qualityType}
        projects={projects}
        workerSkills={workerSkills}
        onTagNameChange={setTagName}
        onDescriptionChange={setDescription}
        onQualityTypeChange={setQuality}
        onProjectsChange={setProjects}
        onWorkerSkillsChange={setWorkerSkills}
      />

      <Flex gap="2" justify="end">
        <Dialog.Close>
          <Button variant="outline" onClick={() => setShowAddTagModal(false)}>
            Cancel
          </Button>
        </Dialog.Close>
        <Dialog.Close>
          <Tooltip content={allowSaveTooltip}>
            <Button
              onClick={useCreateAndUpdateTag}
              loading={isCreatingTag || isUpdatingTags}
              disabled={emptyDescriptionIfQualityTag}
            >
              Create Tag
            </Button>
          </Tooltip>
        </Dialog.Close>
      </Flex>
    </Flex>
  );
};
